import { useRef, useState, useEffect, useCallback } from 'react';
import UnifiedWebSocketService from '../services/UnifiedWebSocketService';

/**
 * useUnifiedAudioRecorder - Enhanced hook for unified audio recording
 * Supports all audio sources (Zoom, Twilio, In-Person) with the new WebSocket architecture
 */
export default function useUnifiedAudioRecorder() {
    const [isConnected, setIsConnected] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [connectionState, setConnectionState] = useState('disconnected');
    const [currentSource, setCurrentSource] = useState(null);
    const [sessionId, setSessionId] = useState(null);
    const [error, setError] = useState(null);
    const [transcription, setTranscription] = useState('');
    const [visitNote, setVisitNote] = useState(null);
    const [audioSegments, setAudioSegments] = useState([]);
    const [processingStatus, setProcessingStatus] = useState(null);
    const [audioLevel, setAudioLevel] = useState(0);
    const [microphoneStatus, setMicrophoneStatus] = useState('inactive');
    
    // Event listeners cleanup refs
    const unsubscribeRefs = useRef([]);
    
    /**
     * Initialize WebSocket connection for audio recording
     */
    const initializeConnection = useCallback(async (source, caseId, options = {}) => {
        try {
            setError(null);
            setCurrentSource(source);
            
            // Connect to unified WebSocket
            await UnifiedWebSocketService.connect(source, caseId, options);
            
        } catch (error) {
            setError(error.message || 'Connection failed');
        }
    }, []);
    
    /**
     * Start audio recording session
     */
    const startRecording = useCallback(async (source, caseId, options = {}) => {
        try {
            setError(null);
            
            // Initialize connection if not already connected
            if (!isConnected || currentSource !== source) {
                await initializeConnection(source, caseId, options);
            }
            
            // Start audio capture
            await UnifiedWebSocketService.startAudioCapture();
            
        } catch (error) {
            setError(error.message);
            throw error;
        }
    }, [isConnected, currentSource, initializeConnection]);
    
    /**
     * Stop audio recording
     */
    const stopRecording = useCallback(async () => {
        try {
            await UnifiedWebSocketService.stopAudioCapture();
        } catch (error) {
            setError(error.message);
        }
    }, []);
    
    /**
     * End the entire session
     */
    const endSession = useCallback(async () => {
        try {
            await UnifiedWebSocketService.endSession();
            
            // Reset local state
            setIsRecording(false);
            setCurrentSource(null);
            setSessionId(null);
            setTranscription('');
            setAudioSegments([]);
            setProcessingStatus(null);
            
        } catch (error) {
            setError(error.message);
        }
    }, []);
    
    /**
     * Pause current audio segment
     */
    const pauseSegment = useCallback(() => {
        UnifiedWebSocketService.pauseSegment();
    }, []);
    
    /**
     * Resume with new audio segment
     */
    const resumeSegment = useCallback(() => {
        UnifiedWebSocketService.resumeSegment();
    }, []);
    
    /**
     * Switch audio source (e.g., from Zoom to phone)
     */
    const switchSource = useCallback((newSource) => {
        UnifiedWebSocketService.switchSource(newSource);
        setCurrentSource(newSource);
    }, []);
    
    /**
     * Manually trigger note generation
     */
    const generateNote = useCallback(() => {
        UnifiedWebSocketService.generateNote();
        setProcessingStatus('generating_note');
    }, []);
    
    /**
     * Send audio data from external sources (Zoom/Twilio)
     */
    const sendAudioData = useCallback((audioData) => {
        if (isRecording) {
            UnifiedWebSocketService.sendAudioData(audioData);
        }
    }, [isRecording]);
    
    /**
     * Get current recording status
     */
    const getStatus = useCallback(() => {
        return UnifiedWebSocketService.getStatus();
    }, []);
    
    // Set up WebSocket event listeners
    useEffect(() => {
        // Connection state changes
        const unsubscribeConnectionState = UnifiedWebSocketService.on('connectionStateChange', (data) => {
            setConnectionState(data.state);
            setIsConnected(data.state === 'connected');
            
            if (data.state === 'error') {
                setError(data.error?.message || 'Connection error');
            }
        });
        
        // Session established
        const unsubscribeSessionEstablished = UnifiedWebSocketService.on('sessionEstablished', (data) => {
            setSessionId(data.session_id);
        });
        
        // Recording started
        const unsubscribeRecordingStarted = UnifiedWebSocketService.on('audioRecordingStarted', () => {
            setIsRecording(true);
            setTranscription(''); // Clear previous transcription when starting new recording
        });
        
        // Recording stopped
        const unsubscribeRecordingStopped = UnifiedWebSocketService.on('audioRecordingStopped', () => {
            setIsRecording(false);
        });
        
        // Audio segment uploaded
        const unsubscribeSegmentUploaded = UnifiedWebSocketService.on('segmentUploaded', (data) => {
            setAudioSegments(prev => [...prev, {
                segmentNumber: data.segment_number,
                s3Url: data.s3_url,
                duration: data.duration_seconds,
                fileSize: data.file_size_bytes,
                uploadedAt: new Date().toISOString()
            }]);
        });

        // Real-time transcription updates
        const unsubscribeTranscription = UnifiedWebSocketService.on('transcriptionUpdate', (data) => {
            const newText = data.transcript || data.text || '';
            const confidence = data.confidence || 0;

            // Skip empty transcripts
            if (!newText) return;

            setTranscription(prev => {
                // PRIORITY 1: Formatted final (lock it in, NEVER overwrite)
                // Only commit when BOTH is_turn_complete AND turn_is_formatted are true
                if (data.turn_is_formatted && data.is_turn_complete) {
                    return prev ? prev + '\n\n• ' + newText : '• ' + newText;
                }

                // PRIORITY 2: Utterance for early display (before turn fully ends)
                if (data.utterance && !data.is_turn_complete) {
                    // Replace last interim segment
                    const lastBulletIdx = prev.lastIndexOf('\n\n• ');
                    if (lastBulletIdx >= 0) {
                        return prev.substring(0, lastBulletIdx) + '\n\n• ' + newText;
                    }
                    return '• ' + newText;
                }

                // PRIORITY 3: Interim update with turn complete but NOT yet formatted
                // This is the unformatted complete turn - just update the last segment
                // The formatted version will arrive shortly and be committed as final
                if (data.is_turn_complete && !data.turn_is_formatted) {
                    const lastBulletIdx = prev.lastIndexOf('\n\n• ');
                    if (lastBulletIdx >= 0) {
                        return prev.substring(0, lastBulletIdx) + '\n\n• ' + newText;
                    }
                    return '• ' + newText;
                }

                // PRIORITY 4: Interim update (keep updating last segment)
                const lastBulletIdx = prev.lastIndexOf('\n\n• ');
                if (lastBulletIdx >= 0) {
                    return prev.substring(0, lastBulletIdx) + '\n\n• ' + newText;
                }
                return newText;
            });
        });

        // Visit note ready
        const unsubscribeVisitNote = UnifiedWebSocketService.on('visitNoteReady', (data) => {
            setVisitNote({
                id: data.note_id,
                content: data.note_content,
                timestamp: data.timestamp,
                sessionId: data.session_id,
                audioSegments: data.audio_segments,
                totalDuration: data.total_duration,
                audioSources: data.audio_sources
            });
            setProcessingStatus('completed');
        });
        
        // Processing started
        const unsubscribeProcessingStarted = UnifiedWebSocketService.on('processingStarted', () => {
            setProcessingStatus('processing');
        });
        
        // Note generation error
        const unsubscribeNoteError = UnifiedWebSocketService.on('noteGenerationError', (data) => {
            // Provide better error messages for common issues
            let errorMessage = 'Note generation failed';
            
            if (data.error?.includes('No transcript content found') || data.detail?.includes('no completed transcripts')) {
                errorMessage = 'No audio transcripts available yet. Please record some audio first or wait for transcription to complete.';
            } else if (data.error?.includes('transcript') || data.detail?.includes('transcript')) {
                errorMessage = 'Audio transcription is still processing. Please wait a moment and try again.';
            } else if (data.error) {
                errorMessage = `Note generation failed: ${data.error}`;
            } else if (data.detail) {
                errorMessage = `Note generation failed: ${data.detail}`;
            }
            
            setError(errorMessage);
            setProcessingStatus('error');
        });
        
        // Server errors
        const unsubscribeServerError = UnifiedWebSocketService.on('serverError', (data) => {
            setError(`Server error: ${data.error || data.message}`);
        });
        
        // Audio recording errors
        const unsubscribeAudioError = UnifiedWebSocketService.on('audioRecordingError', (data) => {
            setError(`Audio recording error: ${data.error}`);
            setIsRecording(false);
        });
        
        // Reconnection events
        const unsubscribeReconnecting = UnifiedWebSocketService.on('reconnecting', () => {
            setConnectionState('reconnecting');
        });
        
        const unsubscribeReconnectionFailed = UnifiedWebSocketService.on('reconnectionFailed', () => {
            setError('Connection lost and reconnection failed');
            setConnectionState('error');
        });
        
        // Audio level monitoring
        const unsubscribeAudioLevel = UnifiedWebSocketService.onAudioLevel((data) => {
            setAudioLevel(data.level);
            setMicrophoneStatus(data.microphoneStatus);
        });
        
        // Store unsubscribe functions
        unsubscribeRefs.current = [
            unsubscribeConnectionState,
            unsubscribeSessionEstablished,
            unsubscribeRecordingStarted,
            unsubscribeRecordingStopped,
            unsubscribeSegmentUploaded,
            unsubscribeTranscription,
            unsubscribeVisitNote,
            unsubscribeProcessingStarted,
            unsubscribeNoteError,
            unsubscribeServerError,
            unsubscribeAudioError,
            unsubscribeReconnecting,
            unsubscribeReconnectionFailed,
            unsubscribeAudioLevel
        ];
        
        // Cleanup function
        return () => {
            unsubscribeRefs.current.forEach(unsubscribe => {
                if (typeof unsubscribe === 'function') {
                    unsubscribe();
                }
            });
            unsubscribeRefs.current = [];
        };
    }, []);
    
    // Cleanup on unmount only
    useEffect(() => {
        return () => {
            // Stop recording and disconnect when component unmounts
            UnifiedWebSocketService.stopAudioCapture();
            UnifiedWebSocketService.disconnect();
        };
    }, []); // Empty dependency array - only run on unmount
    
    return {
        // Connection state
        isConnected,
        connectionState,
        currentSource,
        sessionId,
        error,
        
        // Recording state
        isRecording,
        audioSegments,
        processingStatus,
        
        // Transcription and notes
        transcription,
        visitNote,
        
        // Audio visualization
        audioLevel,
        microphoneStatus,
        
        // Actions
        startRecording,
        stopRecording,
        endSession,
        pauseSegment,
        resumeSegment,
        switchSource,
        generateNote,
        sendAudioData,
        getStatus,
        
        // Utilities
        clearError: () => setError(null),
        clearTranscription: () => setTranscription(''),
        clearVisitNote: () => setVisitNote(null)
    };
}